// Core types for the Koru Habit Tracker Application

export interface User {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
  settings: UserSettings;
}

export interface UserSettings {
  timezone: string;
  notifications: {
    dailyReminder: boolean;
    streakMilestones: boolean;
    weeklyReport: boolean;
  };
  theme: 'light' | 'dark' | 'system';
}

// Authentication types matching API DTOs
export interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  lastLoginAt?: string;
  avatar?: string;
  timeZone?: string; // IANA timezone from backend
  hasCompletedFirstLoginSetup: boolean;
  isEmailVerified: boolean;
  emailVerifiedAt?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  token?: string;
  expiresAt?: string;
  user?: UserInfo;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  timeZone?: string; // optional; auto-detected on the client and sent to API
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface GoogleSignInRequest {
  email: string;
  firstName: string;
  lastName: string;
  googleId: string;
  avatar?: string;
}

export interface AppleSignInRequest {
  email: string;
  firstName: string;
  lastName: string;
  appleUserId: string;
  identityToken: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  avatar?: string;
  timeZone?: string; // optional; allow updating timezone from profile
  hasCompletedFirstLoginSetup?: boolean;
}

export interface Habit {
  id: string;
  name: string;
  description: string;
  iconName: string;
  colorCode: string;
  frequency: HabitFrequency;
  targetStreakLength: number;
  isActive: boolean;
  createdAt: string;
  currentStreak: number;
  longestStreak: number;
  completionRate: number;
  isCompletedToday: boolean;
  
  // Frequency-specific properties
  weeklyDays?: number[]; // For WeeklySpecificDays (0=Sunday, 1=Monday, etc.)
  weeklyCount?: number; // For WeeklyCount
  intervalDays?: number; // For IntervalBased
}

export interface HabitCompletion {
  id: string;
  completedDate: string; // UTC ISO string from API
  notes?: string;
  habitId: string;
  completedDateLocal?: string; // Localized ISO or display string provided by API
  createdAtLocal?: string; // Localized ISO or display string provided by API
}

// Habit Notes types
export interface HabitNote {
  id: string;
  title?: string;
  content: string;
  habitId: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateHabitNoteRequest {
  title?: string;
  content: string;
}

export interface UpdateHabitNoteRequest {
  title?: string;
  content?: string;
}

export interface StreakStats {
  currentStreak: number;
  longestStreak: number;
  totalCompletions: number;
  completionRate: number;
  lastCompletedDate?: string;
  streakStartDate?: string;
}

export enum HabitFrequency {
  Daily = 'daily',
  WeeklySpecificDays = 'weeklySpecificDays',
  WeeklyCount = 'weeklyCount',
  IntervalBased = 'intervalBased'
}

export interface WeeklySpecificDaysConfig {
  days: number[]; // 0-6, Sunday to Saturday
}

export interface WeeklyCountConfig {
  targetCount: number; // e.g., 3 times per week
}

export interface IntervalBasedConfig {
  intervalDays: number; // e.g., every 3 days
}

export type FrequencyConfig = 
  | WeeklySpecificDaysConfig 
  | WeeklyCountConfig 
  | IntervalBasedConfig;

export interface HabitFormData {
  name: string;
  description: string;
  iconName: string;
  colorCode: string;
  frequency: HabitFrequency;
  targetStreakLength: number;
  frequencyConfig?: FrequencyConfig;
}

export interface DashboardStats {
  totalHabits: number;
  activeStreaks: number;
  completedToday: number;
  longestStreak: number;
  weeklyCompletionRate: number;
}

export interface CalendarDay {
  date: string;
  isCompleted: boolean;
  isToday: boolean;
  isCurrentMonth: boolean;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Error types
export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, any>;
}

// Form validation types
export interface ValidationError {
  field: string;
  message: string;
}

// Notification types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
}

// Notification Preferences types
export enum NotificationType {
  HabitReminder = 0,
  StreakMilestone = 1,
  MissedHabit = 2,
  WeeklyReport = 3,
  Achievement = 4,
  StreakEndanger = 5,
  Motivation = 6
}

export enum NotificationChannel {
  InApp = 0,
  BrowserPush = 1,
  Email = 2,
  SMS = 3
}

export enum NotificationStatus {
  Scheduled = 0,
  Sent = 1,
  Failed = 2,
  Cancelled = 3
}

export interface NotificationPreference {
  id: string;
  type: NotificationType;
  isEnabled: boolean;
  preferredTime?: string; // TimeSpan as string (e.g., "09:00:00")
  timeZone?: string;
  channel: NotificationChannel;
  settings: Record<string, any>;
}

export interface UpdateNotificationPreferenceRequest {
  isEnabled: boolean;
  preferredTime?: string;
  timeZone?: string;
  channel: NotificationChannel;
  settings?: Record<string, any>;
}

export interface NotificationHistoryItem {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  actionText?: string;
  actionUrl?: string;
  createdAt: string;
  sentAt?: string;
  status: NotificationStatus;
}

// Subscription types
export interface Subscription {
  id: string;
  userId: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  startDate: string;
  endDate?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialStart?: string;
  trialEnd?: string;
  gracePeriodStart?: string;
  gracePeriodEnd?: string;
  isInTrial: boolean;
  isInGracePeriod: boolean;
  hasActiveAccess: boolean;
  stripePriceId?: string;
  source?: SubscriptionSource;
  createdAt: string;
  updatedAt?: string;
}

export enum SubscriptionTier {
  Free = 0,
  Basic = 1,
  Pro = 2
}

export enum SubscriptionStatus {
  Active = 0,
  Canceled = 1,
  PastDue = 2,
  Unpaid = 3,
  Incomplete = 4,
  IncompleteExpired = 5,
  Trialing = 6
}

export enum SubscriptionSource {
  Stripe = 0,
  Apple = 1
}

export interface SubscriptionPlan {
  priceId: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: string; // monthly, yearly
  tier: SubscriptionTier;
  features: string[];
  isPopular?: boolean;
  discountPercentage?: number;
}

export interface BillingHistoryItem {
  id: string;
  stripeInvoiceId: string;
  amount: number;
  currency: string;
  status: BillingStatus;
  description?: string;
  billingDate: string;
  paidDate?: string;
  failureReason?: string;
  createdAt: string;
}

export enum BillingStatus {
  Draft = 0,
  Open = 1,
  Paid = 2,
  Uncollectible = 3,
  Void = 4
}

export interface FeatureAccess {
  canAccessAnalytics: boolean;
  canAccessWeeklyInsights: boolean;
  canExportData: boolean;
  hasPrioritySupport: boolean;
  canCreateMoreHabits: boolean;
  currentHabitCount: number;
  maxHabitsAllowed: number;
  currentTier: SubscriptionTier;
  hasActiveSubscription: boolean;
  isInTrial: boolean;
  isInGracePeriod: boolean;
  isTrialExpired: boolean;
  trialDaysRemaining?: number;
  gracePeriodDaysRemaining?: number;
  // AI usage tracking
  aiSuggestionsUsedToday: number;
  maxAiSuggestionsPerDay: number;
  canUseAiToday: boolean;
}

// Subscription API request types
export interface CreateSubscriptionRequest {
  priceId: string;
  paymentMethodId?: string;
}

export interface UpdateSubscriptionRequest {
  newPriceId: string;
}

export interface CancelSubscriptionRequest {
  cancelAtPeriodEnd?: boolean;
  cancellationReason?: string;
}

// Feature types for access control
export enum FeatureType {
  BasicHabits = 0,
  Analytics = 1,
  Export = 2,
  PrioritySupport = 3,
  HabitLimit = 4,
  AiSuggestion = 5,
  WeeklyInsights = 6
}

// Support Ticket types
export interface SupportTicket {
  id: string;
  subject: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt?: string;
  resolution?: string;
  resolvedAt?: string;
  userName?: string;
  userEmail?: string;
}

export interface CreateSupportTicketRequest {
  subject: string;
  description: string;
  category: SupportCategory;
  currentUrl?: string;
  userAgent?: string;
}

export interface SupportTicketCreateResponse {
  success: boolean;
  message: string;
  ticket?: SupportTicket;
}

export enum SupportCategory {
  AccountIssues = 0,
  HabitTracking = 1,
  StreakProblems = 2,
  NotificationIssues = 3,
  SubscriptionBilling = 4,
  PerformanceIssues = 5,
  FeatureRequest = 6,
  BugReport = 7,
  Other = 8
}

export enum SupportStatus {
  Open = 'Open',
  InProgress = 'InProgress',
  WaitingForUser = 'WaitingForUser',
  Resolved = 'Resolved',
  Closed = 'Closed'
}

export enum SupportPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical'
}

export interface SupportCategoryInfo {
  value: string;
  displayName: string;
  description: string;
}

// Public Contact Form types (for unauthenticated users)
export interface ContactRequest {
  name: string;
  email: string;
  subject: string;
  message: string;
  userAgent?: string;
  currentUrl?: string;
}

export interface ContactResponse {
  success: boolean;
  message: string;
}

// Email Verification types
export interface SendVerificationEmailRequest {
  email: string;
}

export interface VerifyEmailRequest {
  email: string;
  otpCode: string;
}

export interface EmailVerificationResponse {
  success: boolean;
  message: string;
  expiresAt?: string;
  remainingAttempts?: number;
}

export interface EmailVerificationStatus {
  success: boolean;
  isVerified: boolean;
  hasPendingVerification: boolean;
  expiresAt?: string;
  remainingAttempts: number;
}

// Account Deletion types
export interface InitiateAccountDeletionRequest {
  password: string;
  confirmationText: string;
}

export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  deletedAt?: string;
  permanentDeletionDate?: string;
}

export interface CancelDeletionResponse {
  success: boolean;
  message: string;
}

export interface AccountDeletionStatusResponse {
  isPendingDeletion: boolean;
  deletedAt?: string;
  permanentDeletionDate?: string;
  daysRemaining: number;
}

// Password Reset types
export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  otpCode: string;
  newPassword: string;
}

export interface PasswordResetResponse {
  success: boolean;
  message: string;
  expiresAt?: string;
  remainingAttempts?: number;
}

// ===== Weekly Insights =====

export interface WeeklyMetrics {
  weeklyCompletionRate: number;
  activeStreaks: number;
  totalCompletions: number;
  longestStreak: number;
  topHabitName?: string;
}

export interface HabitInsight {
  habitId: string;
  habitName: string;
  colorCode: string;
  iconName: string;
  completionsThisWeek: number;
  currentStreak: number;
  highlight: string;
  trend: 'up' | 'down' | 'stable';
}

export interface WeeklyInsightResponse {
  summary: string;
  habitInsights: HabitInsight[];
  metrics: WeeklyMetrics;
  generatedAt: string;
  weekStart: string;
  weekEnd: string;
} 